import { IconWrapper, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AiGeneratedButton from './Buttons/AiGeneratedButton';
import IncludeImageButton from './Buttons/IncludeImageButton';
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { interactionEvent } from '../../../Auth/auth.events';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';

interface InteractiveButtonsProps {
  onDeleteClick?: () => void;
  handleReplaceClick?: () => void;
  addAlternateQuestionsClick?: () => void;
  showAIButton?: boolean;
  handleAIPrompt?: (prompt: string) => void;
  showIncludeImageButton?: boolean;
  isIncludeImageChecked?: boolean;
  handleIncludeImageClick?: () => void;
  stackComponents?: boolean;
}

export const InteractiveButtons = ({
  onDeleteClick,
  handleReplaceClick,
  addAlternateQuestionsClick,
  showAIButton,
  handleAIPrompt,
  showIncludeImageButton,
  isIncludeImageChecked,
  handleIncludeImageClick,
  stackComponents,
}: InteractiveButtonsProps) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexDirection: 'row',
        gap: {
          xs: pxToRem(10),
          md: pxTovW(14),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flex: 1,
        }}
      >
        {showAIButton && <AiGeneratedButton handlePrompt={handleAIPrompt} />}
        {showIncludeImageButton && !stackComponents && (
          <Box sx={{ ml: 'auto' }}>
            <IncludeImageButton
              isChecked={isIncludeImageChecked}
              onClick={handleIncludeImageClick}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        {addAlternateQuestionsClick && (
          <Button
            sx={{
              color: '#007CDC',
              border: '1px solid #007CDC',
              borderRadius: {
                md: '16px',
                xs: '50%',
              },
              p: {
                md: '1px 8px',
                xs: '4px 4px',
              },
              cursor: 'pointer',
              background: '#E2F2FF',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            onClick={async (ev) => {
              ev.stopPropagation();
              addAlternateQuestionsClick && addAlternateQuestionsClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'ADD_ALTERNATE_QUESTION',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
          >
            <IconWrapper
              name="plus_icon"
              customSx={{ height: '13px', width: '13px' }}
              type="png"
              parentFolder="icons"
            />
            {!isMobile && (
              <Typography color="#007CDC" variant="smallText">
                Add Alternate Questions
              </Typography>
            )}
          </Button>
        )}
        {onDeleteClick && !stackComponents && (
          <Button
            onClick={async () => {
              onDeleteClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'DELETE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={{
              cursor: 'pointer',
              p: 0,
            }}
          >
            <IconWrapper
              name="remove"
              size="md"
              type="png"
              parentFolder="icons"
            />
          </Button>
        )}
        {handleReplaceClick && !stackComponents && (
          <Button
            onClick={async () => {
              handleReplaceClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'REPLACE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={{
              cursor: 'pointer',
              p: 0,
            }}
          >
            <IconWrapper
              name="refresh"
              size="md"
              type="png"
              parentFolder="icons"
            />
          </Button>
        )}
        <IconWrapper
          name="reorder"
          size="small"
          type="png"
          parentFolder="icons"
        />
        {stackComponents && (
          <Button
            onClick={(ev) => {
              ev?.stopPropagation();
              setAnchorEl(ev.currentTarget);
            }}
            sx={{
              p: 0,
            }}
          >
            <IconWrapper
              name="triple-horizontal-dots"
              size="small"
              parentFolder="icons"
              type="png"
              customSx={{
                transform: 'rotate(90deg)',
                height: '12px',
              }}
            />
          </Button>
        )}
      </Box>
      <Popover
        id={'popover-interactive-buttons'}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPopover-root': {},
          '& .MuiPopover-paper': {
            // width: { xs: pxTovW(277), md: pxTovW(183), lg: pxTovW(183) },
            borderRadius: { xs: pxToRem(10), md: pxToRem(10) },
            border: '1px solid #75C3FF',
            backgroundColor: '#F8FCFF',
          },
        }}
      >
        <Box>
          <Button
            onClick={async (event) => {
              setAnchorEl(null);
              event.stopPropagation();
              onDeleteClick && onDeleteClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'DELETE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderBottom: '1px solid #F1F1F1',
              width: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <IconWrapper
              name="remove"
              size="md"
              type="png"
              parentFolder="icons"
            />
            <Typography variant="h4" fontWeight="medium">
              Delete
            </Typography>
          </Button>
        </Box>
        <Box>
          <Button
            onClick={async (event) => {
              setAnchorEl(null);
              event.stopPropagation();
              handleReplaceClick && handleReplaceClick();
              await interactionEvent({
                context: 'Review_question_paper',
                name: 'REPLACE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderBottom: '1px solid #F1F1F1',
              width: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <IconWrapper
              name="refresh"
              size="md"
              type="png"
              parentFolder="icons"
            />
            <Typography variant="h4" fontWeight="medium">
              Replace Question
            </Typography>
          </Button>
        </Box>
        {showIncludeImageButton && (
          <Box>
            <Button
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                width: '100%',
                justifyContent: 'flex-start',
              }}
              onClick={(event) => {
                setAnchorEl(null);
                event.stopPropagation();
                handleIncludeImageClick && handleIncludeImageClick();
              }}
            >
              <Box
                sx={{
                  height: '12px',
                  width: '12px',
                  backgroundColor: '#fff',
                  borderRadius: '3px',
                  border: '1px solid #007CDC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isIncludeImageChecked && (
                  <CheckIcon
                    sx={{
                      height: '10px',
                      width: '10px',
                      color: '#000',
                      stroke: '#000',
                      strokeWidth: 2,
                    }}
                  />
                )}
              </Box>
              <Typography variant="h4" fontWeight="medium">
                Include Image
              </Typography>
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
};
